import affirm from '../../../core/Affirm'
import algo from '../../../core/Algo'
import { IFormField } from '../../../definitions/FormField'
import { IFormGroup } from '../../../definitions/FormGroup'
import Helper from '../../../helpers/Helper'

const ContractFormHelper = {
    getValue: (item: any, path: string, index: number, step: IFormGroup) => {
        const parts = path.split('.')
        if (parts.length > 1) {
            const prevPathKey = parts.slice(0, -1).join('.')
            const allFields = step.fields.flatMap(x => ContractFormHelper.flatFields(x))
            const prevField = allFields.find(x => x.key === prevPathKey)
            if (prevField && prevField.type === 'list') {
                const childKey = parts.at(-1)
                const childValue = Helper.getNestedValue(item, childKey)
                return childValue
            } else {
                return Helper.getNestedValue(item, path)
            }
        } else {
            return Helper.getNestedValue(item, path)
        }
    },
    setValue: (item: any, path: string, newValue: any, step: IFormGroup) => {
        const parts = path.split('.')
        if (parts.length > 1) {
            const prevPathKey = parts.slice(0, -1).join('.')
            const allFields = step.fields.flatMap(x => ContractFormHelper.flatFields(x))
            const prevField = allFields.find(x => x.key === prevPathKey)
            if (prevField && prevField.type === 'list') {
                const myPath = parts.at(-1)
                return Helper.setNestedValue(structuredClone(item), myPath, newValue)
            } else {
                return Helper.setNestedValue(structuredClone(item), path, newValue)
            }
        } else {
            return Helper.setNestedValue(structuredClone(item), path, newValue)
        }
    },
    flatFields: (field: IFormField): IFormField[] => {
        return [
            field,
            ...(field.childFields?.flatMap(x => ContractFormHelper.flatFields(x)) ?? [])
        ]
    },
    flatSteps: (step: IFormGroup): IFormField[] => {
        if (!algo.hasVal(step.fields))
            return []
        const flattenStep: IFormField[] = []
        step.fields.forEach((field, i) => {
            affirm(field, `Invalid field in step "${step.label}" at index ${i}`)

            flattenStep.push(field)
            if (field.childFields)
                flattenStep.push(...ContractFormHelper.flatSteps({ label: '', fields: field.childFields }))
            if (field.objectFields)
                flattenStep.push(...ContractFormHelper.flatSteps({ label: '', fields: field.objectFields }))
        })
        return flattenStep
    },
    validate: (steps: IFormGroup[]): string[] => {
        affirm(steps, 'Invalid steps')

        const flattenFields = steps.map(x => ContractFormHelper.flatSteps(x)).flat()
        const errors: string[] = []

        for (let i = 0; i < steps.length; i++) {
            const step = steps[i]
            if (!algo.hasVal(step.fields))
                errors.push(`Invalid fields for step "${step.label}": can't be null or undefined`)
        }

        for (let i = 0; i < flattenFields.length; i++) {
            const field = flattenFields[i]

            if (!algo.hasVal(field)) {
                errors.push(`Invalid field at index ${field.label}: can't be null or undefined`)
                continue
            }
            if (!field.label || !field.key)
                errors.push(`Error on key "${field.key}": empty "label" or "key"`)
            if (flattenFields.filter(x => x.key === field.key).length > 1)
                errors.push(`Error on key "${field.key}": duplicate "key"`)
            if (field.validation && field.validation.isRequired === true && field.validation.exclusiveWith?.length > 0)
                errors.push(`Error on key "${field.key}": can't have both "isRequired" and "exclusiveWith"`)
            if (field.type === 'object' && field.childFields)
                errors.push(`Error on key "${field.key}": field type "${field.type}" can't have "childFields" but must have "objectFields"`)
            if (field.type === 'object' && (!field.objectFields || field.objectFields?.length === 0))
                errors.push(`Error on key "${field.key}": field type "${field.type}" must have atleast one "objectFields"`)
            if (field.type === 'list' && field.objectFields)
                errors.push(`Error on key "${field.key}": field type "${field.type}" can't have "objectFields" but must have "childFields"`)
            if (field.type === 'list' && (!field.childFields || field.childFields?.length === 0))
                errors.push(`Error on key "${field.key}": field type "${field.type}" must have atleast one "childFields"`)
        }
        return errors
    },
    validateField: (field: IFormField, value: any, item: any, index: number, step: IFormGroup) => {
        if (!algo.hasVal(field.validation)) {
            return { isError: false, helperText: null }
        } else {
            if (!Helper.hasVal(value) || ((field.type === 'multi-system-config' || field.type === 'list') && value.length === 0)) {
                if (field.validation.isRequired)
                    return { isError: true, helperText: `${field.label} is required` }
                else
                    return { isError: false, helperText: null }
            } else if (field.validation.exclusiveWith) {
                const flattenStep = ContractFormHelper.flatSteps(step)
                // @ts-ignore For whatever unknown to god reason, typescript decides to throw an error (while running not in the editor) complaining that some non-existent types don't match...
                const exclusiveValues: { field: IFormField, value: any }[] = field.validation.exclusiveWith.map(key => ({
                    field: flattenStep.find(x => x.key === key),
                    value: ContractFormHelper.getValue(item, key, index, step)
                }))
                const allValues = [...exclusiveValues, { field, value }]
                const onlyWithValues = allValues.filter(x => x.value && (algo.hasVal(x.value) && x.value !== '' && x.value.length !== 0 && x.value !== false))
                if (onlyWithValues.length !== 1) {
                    return { isError: true, helperText: `This field is exclusive with ${exclusiveValues.map(x => x.field.label).join(', ')}` }
                } else
                    return { isError: false, helperText: null }
            } else
                return { isError: false, helperText: null }
        }
    }

}

export default ContractFormHelper
